'use client';

import { useCallback, useSyncExternalStore } from 'react';
import { apiFetch } from '@/lib/api-client';
import { getToken, setToken, removeToken, setStoredUser, onAuthChange } from '@/lib/auth';
import { withBasePath } from '@/lib/base-path';
import type { AuthResponse, User } from '@/types';

const USER_KEY = 'instask_user';

let cachedUserRaw: string | null = null;
let cachedUser: User | null = null;

function getSnapshotUser(): User | null {
  if (typeof window === 'undefined') return null;
  const raw = localStorage.getItem(USER_KEY);
  if (raw === cachedUserRaw) return cachedUser;
  cachedUserRaw = raw;
  try {
    cachedUser = raw ? (JSON.parse(raw) as User) : null;
  } catch {
    cachedUser = null;
  }
  return cachedUser;
}

export function useAuth() {
  const token = useSyncExternalStore(onAuthChange, getToken, () => null);
  const user = useSyncExternalStore(onAuthChange, getSnapshotUser, () => null);

  const login = useCallback(async (email: string, password: string) => {
    const res = await apiFetch<AuthResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    setToken(res.token);
    setStoredUser(res.user);
    return res;
  }, []);

  // Login vía Instance (Seller Center). El hash de la password y la api key
  // los maneja nuestro backend; desde acá viaja igual que el login normal.
  const loginInstance = useCallback(async (email: string, password: string) => {
    const res = await apiFetch<AuthResponse>('/auth/login/instance', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    setToken(res.token);
    setStoredUser(res.user);
    return res;
  }, []);

  const logout = useCallback(() => {
    removeToken();
    window.location.href = withBasePath('/login');
  }, []);

  return { user, isAuthenticated: !!token, login, loginInstance, logout };
}
